import React, { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import classNames from 'classnames';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, footer, size = 'md' }) => {
  const { mode } = useSelector((state: RootState) => state.theme);

  const widthClass = size === 'sm'
    ? 'max-w-md'
    : size === 'lg'
    ? 'max-w-3xl'
    : 'max-w-xl';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal Panel */}
          <motion.div
            className={classNames(
              'fixed inset-0 z-50 flex items-center justify-center p-4',
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={classNames(
                'w-full rounded-lg shadow-xl overflow-hidden max-h-[100vh] flex flex-col',
                widthClass,
                {
                  'bg-white': mode === 'light',
                  'bg-gray-900': mode === 'dark',
                }
              )}
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 20, stiffness: 250 }}
            >
              <div
                className={classNames('px-4 py-3 border-b', {
                  'border-gray-200': mode === 'light',
                  'border-gray-700': mode === 'dark',
                })}
              >
                <div className="flex items-center justify-between">
                  <h3
                    className={classNames('text-base font-semibold', {
                      'text-gray-800': mode === 'light',
                      'text-gray-100': mode === 'dark',
                    })}
                  >
                    {title}
                  </h3>
                  <button
                    className={classNames(
                      'px-2 py-1 rounded hover:bg-gray-100',
                      {
                        'text-gray-600 hover:text-gray-800': mode === 'light',
                        'text-gray-300 hover:bg-gray-800': mode === 'dark',
                      }
                    )}
                    onClick={onClose}
                  >
                    ✕
                  </button>
                </div>
              </div>
              {/* Content area scrolls while header & footer stay visible */}
              <div className="p-4 overflow-y-auto flex-1 min-h-0">{children}</div>
              {footer && (
                <div
                  className={classNames('px-4 py-3 border-t', {
                    'border-gray-200': mode === 'light',
                    'border-gray-700': mode === 'dark',
                  })}
                >
                  <div className="flex justify-end gap-2">{footer}</div>
                </div>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Modal;